'use client';

import { AnimatePresence, domAnimation, LazyMotion } from 'motion/react';
import * as m from 'motion/react-m';
import dynamic from 'next/dynamic';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type LmsDataWithAdsType } from '@/app/api/lms/type';
import { Container } from '@/app/components/layouts/container';
import { Image } from '@/app/components/media/image';
import { easeBounce } from '@/app/constants/motion';
import { useDisclosure } from '@/app/hooks/useDisclosure';
import { useLineBrowser } from '@/app/hooks/useLineBrowser';
import { useMediaQuery } from '@/app/hooks/useMediaQuery';
import { checkEligibility } from '@/app/lcv/components/campaigns/big-campaign-2026/actions/checkEligibility';
import { claimLuckyDraw } from '@/app/lcv/components/campaigns/big-campaign-2026/actions/claimLuckyDraw';
import { confirmLuckDrawReward } from '@/app/lcv/components/campaigns/big-campaign-2026/actions/confirmLuckyDraw';
import { logTrace } from '@/app/lcv/components/campaigns/big-campaign-2026/actions/logTrace';
import { CAMPAIGN_ID as CLAIM_CAMPAIGN_ID, type CampaignModel, type ClaimLuckyDrawResult, type TrackerResult } from '@/app/lcv/components/campaigns/big-campaign-2026/actions/schema';
import { CAMPAIGN_NAME, FORM_FIELD_LABELS, FORM_ID, KV_IMAGE, RegisterStep, TRACKED_VALUE_FIELDS } from '@/app/lcv/components/campaigns/big-campaign-2026/constants';
import { buildTrace, sanitizeSearch } from '@/app/lcv/components/campaigns/big-campaign-2026/shared/trace';
import { traceStep } from '@/app/lcv/components/campaigns/big-campaign-2026/shared/trace-client';
import { buildShareReceiverDiscount, createBranchCodeFormat, createLeadRemark, findModelByCode, getDiscountByModel, isNonOMO, markNow, msSince, parseBranchCodeParam, RequestTimeoutError, toModelGroup, withTimeout } from '@/app/lcv/components/campaigns/big-campaign-2026/shared/utils';
import { StepForm } from '@/app/lcv/components/campaigns/big-campaign-2026/step/form';
import { preloadSpinBackground } from '@/app/lcv/components/campaigns/big-campaign-2026/step/spin/background';
import styles from '@/app/lcv/components/campaigns/big-campaign-2026/styles.module.css';
import { BIG_CAMPAIGN_2026_EVENT, STEP_NAME, trackBigCampaign2026 } from '@/app/lcv/components/campaigns/big-campaign-2026/tracking';
import type { DealerInformationType, EligibilityType, QueryParamsType, SavedFormInputType, StepFormPropsWithVariant, TierType } from '@/app/lcv/components/campaigns/big-campaign-2026/types';
import { LEAD_FORM_EVENT } from '@/app/lib/forms/tracking/constants';
import { useFormTracking } from '@/app/lib/forms/tracking/useFormTracking';
import { type ProvinceWithDistrictsType } from '@/app/types/form';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';
import { getAdsTracking } from '@/app/utils/lms';
import logger from '@/lib/logger';
import { createLead } from '@/services/lms/createLead';
import { trackLeadInitiated, trackLeadSuccess, type TrackLeadSuccessParams } from '@/services/lms/tracking';
import { useLmsData } from '@/services/lms/useLmsData';
import { type SpecialOffer } from '@/types/special-offer';
const ErrorToast = dynamic(() => import('@/app/lcv/components/campaigns/big-campaign-2026/shared/error-toast').then(mod => mod.ErrorToast), {
  ssr: false
});
const ModalAlreadySubmitted = dynamic(() => import('@/app/lcv/components/campaigns/big-campaign-2026/step/form/modal-already-submitted').then(mod => mod.ModalAlreadySubmitted), {
  ssr: false
});
/** Why the error toast opened (GA `error_popup` `text`). */
type ErrorPopupReason = 'eligibility_failed' | 'eligibility_timeout' | 'not_eligible' | 'claim_error' | 'claim_timeout' | 'lead_error' | 'lead_timeout';
const ModalTrackerNotFound = dynamic(() => import('@/app/lcv/components/campaigns/big-campaign-2026/step/form/modal-not-found').then(mod => mod.ModalTrackerNotFound), {
  ssr: false
});
const StepSummary = dynamic(() => import('@/app/lcv/components/campaigns/big-campaign-2026/step/summary').then(mod => mod.StepSummary), {
  ssr: false
});
const StepSpin = dynamic(() => import('@/app/lcv/components/campaigns/big-campaign-2026/step/spin').then(mod => mod.StepSpin), {
  ssr: false
});
const StepReveal = dynamic(() => import('@/app/lcv/components/campaigns/big-campaign-2026/step/reveal').then(mod => mod.StepReveal), {
  ssr: false
});
const SpecialOffersContent = dynamic(() => import('@/app/lcv/components/campaigns/big-campaign-2026/shared/special-offers/content').then(mod => mod.SpecialOffersContent), {
  ssr: false
});
interface CampaignsBigCampaign2026Props extends StepFormPropsWithVariant {
  provinces: ProvinceWithDistrictsType[];
  branchCodeData: Record<string, DealerInformationType>;
  models: CampaignModel[];
  formName: string;
  queryParams?: QueryParamsType;
  trackerData?: TrackerResult | null;
  preSelectedModelCode?: string;
}
type CampaignsBigCampaign2026PropsWithVariant = (CampaignsBigCampaign2026Props & {
  variant?: 'default';
}) | (CampaignsBigCampaign2026Props & {
  variant: 'offer';
  offerDetail: StepFormPropsWithVariant['offerDetail'];
});
const NO_ANIMATION_VARIANTS = {
  enter: {
    opacity: 1
  },
  center: {
    opacity: 1
  },
  exit: {
    opacity: 1
  }
};
const DESKTOP_VARIANTS = {
  enter: {
    opacity: 0
  },
  center: {
    opacity: 1
  },
  exit: {
    opacity: 0
  }
};
const VARIANTS = {
  enter: {
    x: '100%',
    opacity: 0
  },
  center: {
    x: '0%',
    opacity: 1
  },
  exit: {
    x: '-100%',
    opacity: 0
  }
};
export const CampaignsBigCampaign2026 = ({
  variant = 'default',
  imageCover = KV_IMAGE,
  provinces = [],
  branchCodeData = {},
  models = [],
  formName,
  offerDetail,
  preSelectedModelCode,
  trackerData,
  queryParams: initialQueryParams
}: CampaignsBigCampaign2026PropsWithVariant) => {
  const isLine = useLineBrowser();
  const isDesktop = useMediaQuery('(min-width: 1280px)');
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const queryParams = initialQueryParams || {
    scId: '',
    branchCode: '',
    // share link params
    refTrackerId: '',
    leadId: ''
  };
  const hasRefTrackerId = !!queryParams.refTrackerId;
  const isFlowShareLink = hasRefTrackerId && !!trackerData?.trackerId;
  const isInvalidTrackerId = hasRefTrackerId && !trackerData?.trackerId;
  const shareModelCode = queryParams.modelCode || trackerData?.modelCode || '';
  const [step, setStep] = useState<number>(RegisterStep.form);
  const [savedForm, setSavedForm] = useState<SavedFormInputType | undefined>(undefined);
  const [savedTracking, setSavedTracking] = useState<TrackLeadSuccessParams | undefined>(undefined);

  // The customer's own eligibility (a receiver's prices their model).
  const [eligibility, setEligibility] = useState<EligibilityType | undefined>(undefined);

  // Share link: the sharer's model group and the claim (kept out of `eligibility`).
  const sharerModelGroup = findModelByCode(models, shareModelCode)?.modelGroup;
  const [claimResult, setClaimResult] = useState<ClaimLuckyDrawResult>();
  const [omoQrCode, setOmoQrCode] = useState<string | undefined>(undefined);
  const [isRetryingQrCode, setIsRetryingQrCode] = useState<boolean>(false);
  const [isShowReveal, setIsShowReveal] = useState<boolean>(isFlowShareLink);
  const [isShowSpin, setIsShowSpin] = useState<boolean>(false);

  // Tracking step: the spin overlay sits on the form step.
  const trackingStepId = isShowSpin ? RegisterStep.spin : step;

  // `lead-*` events; on an offer page the offer is the form.
  const trackingStep = {
    stepName: STEP_NAME[trackingStepId],
    fieldLabels: FORM_FIELD_LABELS,
    valueFields: TRACKED_VALUE_FIELDS,
    // Province / district uid → name.
    formatValue: (field: string, value: unknown) => {
      if (field === 'province') {
        return provinces.find(p => p.uid === value)?.name_th;
      }
      if (field === 'district') {
        return provinces.flatMap(p => p.districts ?? []).find(d => d.uid === value)?.name_th;
      }
      return undefined;
    },
    // A valid submit goes to the eligibility check.
    submitPassValue: 'eligible'
  };
  const formTracker = useFormTracking(variant === 'offer' && offerDetail ? {
    formId: offerDetail.uid,
    formName,
    formType: 'big_campaign_2026_offer',
    offerUrl: offerDetail.url,
    ...trackingStep
  } : {
    formId: FORM_ID,
    formName,
    formType: 'big_campaign_2026',
    ...trackingStep
  });
  const [isCollecting, setIsCollecting] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isNavigatingToSummary, setIsNavigatingToSummary] = useState<boolean>(false);

  // The last error toast's reason and step (its close-button GA).
  const [errorPopup, setErrorPopup] = useState<{
    reason: ErrorPopupReason;
    step: number;
  }>();
  const [showErrorToast, {
    open: openErrorToast,
    close: closeErrorToast
  }] = useDisclosure(false);
  const [showAlreadySubmitted, {
    open: openAlreadySubmitted,
    close: closeAlreadySubmitted
  }] = useDisclosure(false);
  const selectedModel = useMemo(() => findModelByCode(models, savedForm?.modelCode), [models, savedForm?.modelCode]);
  const preSelectedDealer = useMemo(() => {
    if (isFlowShareLink) return;
    const dealerCode = parseBranchCodeParam(queryParams.branchCode);
    if (!dealerCode) return;

    // Unknown codes leave the dealer selects for the customer.
    return branchCodeData[dealerCode];
  }, [isFlowShareLink, branchCodeData, queryParams.branchCode]);

  // This visit's lead; kept out of `eligibility`, which would re-open the spin.
  const [createdLead, setCreatedLead] = useState<{
    leadId?: string;
    createdAt?: string;
  } | undefined>(undefined);

  // Receiver: own campaign discount + the sharer's my-MEMBER for that group.
  const getReceiverDiscount = (modelGroup?: string, claim: ClaimLuckyDrawResult | undefined = claimResult) => buildShareReceiverDiscount({
    receiverDiscounts: eligibility?.discounts,
    sharerDiscounts: [trackerData?.discounts, claim?.discounts],
    modelGroup,
    sharerModelGroup
  });

  // Coupon data (+ lead id / date). Receiver: sharer's tier and name, mixed discount.
  const summaryData = useMemo(() => {
    if (!eligibility) return eligibility;
    const data: EligibilityType = createdLead ? {
      ...eligibility,
      leadId: createdLead.leadId || eligibility.leadId,
      leadCreatedAt: createdLead.createdAt
    } : eligibility;
    if (!isFlowShareLink) return data;
    const group = toModelGroup(selectedModel?.modelGroup);
    return {
      ...data,
      trackerId: claimResult?.refTrackerId || data.trackerId,
      fullName: claimResult?.refFullName || trackerData?.fullName,
      tier: (claimResult?.refTier ?? trackerData?.tier) as TierType || null,
      // Receiver: no QR.
      qrCodeData: null,
      discounts: {
        ...data.discounts,
        [group]: getReceiverDiscount(group)
      } as EligibilityType['discounts']
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibility, createdLead, claimResult, omoQrCode, selectedModel, isFlowShareLink]);
  const funnelRef = useRef<{
    isResultTracked?: boolean;
    isSummaryTracked?: boolean;
  }>({});
  const eligibilityRequestRef = useRef(0);
  const {
    lmsData,
    onUpdateLms,
    reset: resetLms
  } = useLmsData({
    ctsCampaignId: FORM_ID,
    ctsCampaignName: formName,
    ctsCampaignVersion: '1',
    model: toModelGroup(selectedModel?.modelGroup)
  });
  const paginateTimeoutRef = useRef<number>(undefined);
  const nextPage = (page?: number) => {
    const nextStep = page || step + 1;
    // Execute before next repaint
    const rafId = requestAnimationFrame(() => {
      setStep(nextStep);
    });
    paginateTimeoutRef.current = rafId;
  };

  // Preload the spin background.
  useEffect(() => {
    preloadSpinBackground();
  }, []);

  // Scroll to top when navigating to summary step
  useEffect(() => {
    if (step !== RegisterStep.summary) return;
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [step]);
  const handleFormSubmit = async (data: SavedFormInputType) => {
    if (isSubmitting) return;
    const requestId = ++eligibilityRequestRef.current;
    let startedAt: number | undefined;
    try {
      setIsSubmitting(true);
      setSavedForm(data);
      setCreatedLead(undefined);
      const branchCode = data.dealer;
      const model = findModelByCode(models, data.modelCode);
      const tracking: TrackLeadSuccessParams = {
        type: variant === 'offer' ? 'financial' : 'interest',
        campaignId: FORM_ID,
        campaignName: formName,
        dealer: {
          branchCode: branchCode || '',
          name: preSelectedDealer?.name_th || branchCodeData[branchCode].name_th,
          district: branchCode ? branchCodeData[branchCode].district || '' : '',
          province: branchCode ? branchCodeData[branchCode].province || '' : ''
        },
        selectedModel: model?.model || offerDetail?.model
      };
      setSavedTracking(tracking);
      startedAt = markNow();
      funnelRef.current = {};
      // Receivers check eligibility too, to price their model.
      const result = await withTimeout(checkEligibility(undefined, {
        fullName: data.fullname,
        phoneNumber: data.phoneNumber,
        citizenId: data.citizenId || '',
        type: data.type || 'national_id',
        modelCode: data.modelCode || ''
      }, buildTrace('eligibility', {
        modelFound: !!model
      })));
      if (requestId !== eligibilityRequestRef.current) return;
      const responseData = result.data;
      if (result.error || !responseData) {
        formTracker.onFailed({
          errorType: 'eligibility_error',
          responseMs: msSince(startedAt)
        });
        trackBigCampaign2026({
          step: RegisterStep.form,
          groupName: formName,
          value: 'eligibility_failed'
        });
        handleOnError('eligibility_failed', RegisterStep.form);
        return;
      }
      setEligibility(responseData);

      // A locked prize comes with its QR (fresh ones get it from the confirm). Not for receivers.
      if (responseData.qrCodeData && !isFlowShareLink) {
        setOmoQrCode(responseData.qrCodeData);
      }
    } catch (error) {
      if (requestId !== eligibilityRequestRef.current) return;
      logger.error(error);
      if (startedAt !== undefined) {
        const isTimeout = error instanceof RequestTimeoutError;
        formTracker.onFailed({
          errorType: isTimeout ? 'timeout' : 'eligibility_error',
          responseMs: msSince(startedAt)
        });
        const failedStep = isTimeout ? 'eligibility_timeout' : 'eligibility_failed';
        trackBigCampaign2026({
          step: RegisterStep.form,
          groupName: formName,
          value: failedStep
        });
      }
      eligibilityRequestRef.current++;
      handleOnError(error instanceof RequestTimeoutError ? 'eligibility_timeout' : 'eligibility_failed', RegisterStep.form);
    }
  };
  const onRevealDone = () => {
    setIsShowReveal(false);
  };

  /** Receiver: claim the sharer's prize for the chosen model. Throws after the toast. */
  const claimShare = async (data: SavedFormInputType) => {
    try {
      const result = await withTimeout(claimLuckyDraw(undefined, {
        refTrackerId: trackerData?.trackerId || '',
        refChannel: 'spin_shared_link',
        leadId: queryParams.leadId || '',
        campaign: CLAIM_CAMPAIGN_ID,
        fullName: data.fullname,
        phoneNumber: data.phoneNumber,
        type: data.type,
        citizenId: data.citizenId,
        modelCode: data.modelCode || ''
      }, buildTrace('claim', {
        modelFound: !!findModelByCode(models, data.modelCode)
      })));
      if (result.error || !result.data?.refTrackerId) throw result;
      setClaimResult(result.data);
      return result.data;
    } catch (error) {
      logger.error('[error] claim shared prize', error);
      formTracker.onFailed({
        errorType: error instanceof RequestTimeoutError ? 'timeout' : 'claim_error'
      });
      handleOnError(error instanceof RequestTimeoutError ? 'claim_timeout' : 'claim_error', RegisterStep.spin);
      throw error;
    }
  };

  // Spin CTA: sharer confirms + lead; receiver claims + lead (never confirms).
  const onSpinDone = async () => {
    if (isCollecting || !eligibility || !savedForm) return;
    setIsCollecting(true);
    try {
      if (isFlowShareLink) {
        // Reuse the claim if only the lead failed.
        const claim = claimResult ?? (await claimShare(savedForm));
        await submitLead(savedForm, false, claim);
      } else {
        // Always confirm, even if eligibility returned a QR.
        await confirmPrize(eligibility.trackerId, savedForm.modelCode);
        await submitLead(savedForm, false);
      }
      setIsShowSpin(false);
      skipToSummary();
    } catch {
      // Toast shown; the customer can tap again.
    } finally {
      setIsCollecting(false);
    }
  };
  const skipToSummary = () => {
    setIsNavigatingToSummary(true);
    closeAlreadySubmitted();
    nextPage(RegisterStep.summary);
  };

  /** Error toast + GA `error_popup` (reason in `text`: which step failed). */
  const handleOnError = (reason: ErrorPopupReason, step: number) => {
    setIsSubmitting(false);
    setErrorPopup({
      reason,
      step
    });
    openErrorToast();
    // Trace 11: which popup the customer saw, and why.
    traceStep('errorPopup', {
      popup: 'error_toast',
      reason,
      stepName: STEP_NAME[step] ?? null,
      modelCode: savedForm?.modelCode ?? '',
      refTrackerId: queryParams.refTrackerId || null
    }, 'fail');
    trackBigCampaign2026({
      step,
      groupName: formName,
      value: 'error_popup',
      text: reason
    });
  };
  const handleClearForm = () => {
    resetLms();
    setIsSubmitting(false);
    setSavedForm(undefined);
    setSavedTracking(undefined);
    setIsNavigatingToSummary(false);
    closeAlreadySubmitted();
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  const saveLmsPayload = (data: SavedFormInputType) => {
    onUpdateLms('nationalId', data.citizenId || '');
    onUpdateLms('fullname' as any, data.fullname);
    onUpdateLms('mobile', data.phoneNumber);
    const dealerCode = preSelectedDealer?.branchCode || data.dealer;
    if (!!dealerCode) {
      onUpdateLms('branchCode', createBranchCodeFormat(dealerCode));
    }
    const scId = queryParams.scId;
    if (!!scId) {
      onUpdateLms('scId', scId);
    }
  };
  const submitLead = async (submitData: SavedFormInputType, isSkipToSummary: boolean = true, claim?: ClaimLuckyDrawResult) => {
    let leadStartedAt: number | undefined;
    try {
      const model = findModelByCode(models, submitData.modelCode);
      // Receiver remark = coupon figures.
      const remark = isNonOMO(submitData.dealer) ? createLeadRemark(isFlowShareLink ? getReceiverDiscount(model?.modelGroup, claim) : getDiscountByModel(eligibility?.discounts, model?.modelGroup), model?.model, isFlowShareLink, summaryData?.tier) : '';
      const leadPayload: LmsDataWithAdsType = {
        ...lmsData,
        model: toModelGroup(model?.modelGroup),
        mobile: submitData.phoneNumber,
        firstName: submitData.fullname,
        remark: remark
      };
      if (isFlowShareLink) {
        leadPayload.refTrackerId = queryParams.refTrackerId;
      }
      if (submitData.citizenId) {
        leadPayload.nationalId = submitData.citizenId;
      }
      const dealerCode = preSelectedDealer?.branchCode || submitData.dealer;
      if (!!dealerCode) {
        leadPayload.branchCode = createBranchCodeFormat(dealerCode);
      }
      const scId = queryParams.scId;
      if (!!scId) {
        leadPayload.scId = scId;
      }
      leadStartedAt = performance.now();
      const lead = await withTimeout(createLead({
        ...leadPayload,
        ...getAdsTracking()
      }));
      formTracker.onSuccess({
        responseMs: msSince(leadStartedAt)
      });
      leadStartedAt = undefined;
      traceStep('lead', {
        modelCode: submitData.modelCode ?? '',
        modelGroup: toModelGroup(model?.modelGroup) ?? null,
        refTrackerId: isFlowShareLink ? queryParams.refTrackerId : null,
        leadIdSet: !!lead?.leadId
      }, 'ok');
      setCreatedLead({
        leadId: lead?.leadId,
        createdAt: lead?.createdAt
      });
      resetLms();
      if (isSkipToSummary) {
        skipToSummary();
      }
      return lead;
    } catch (error) {
      traceStep('lead', {
        modelCode: submitData.modelCode ?? '',
        refTrackerId: isFlowShareLink ? queryParams.refTrackerId : null,
        reason: error instanceof RequestTimeoutError ? 'timeout' : 'error'
      }, 'fail');
      if (leadStartedAt !== undefined) {
        const isTimeout = error instanceof RequestTimeoutError;
        formTracker.onFailed({
          errorType: isTimeout ? 'timeout' : 'lead_error',
          responseMs: msSince(leadStartedAt)
        });
        formTracker.onStep(isTimeout ? LEAD_FORM_EVENT.creationTimeout : LEAD_FORM_EVENT.creationFailed);
      }
      handleOnError(error instanceof RequestTimeoutError ? 'lead_timeout' : 'lead_error', RegisterStep.spin);
      throw error;
    }
  };

  /** `POST /lucky-draw`: locks the prize, returns the QR (none → "ลองใหม่อีกครั้ง"). */
  const confirmPrize = async (trackerId?: string, modelCode?: string) => {
    if (!trackerId || !modelCode) {
      // Silent skip: no confirm, but the summary (and Share) still opens.
      traceStep('confirmSkipped', {
        trackerId: trackerId ?? '',
        modelCodeSent: modelCode ?? ''
      }, 'fail');
      return;
    }
    try {
      const result = await withTimeout(confirmLuckDrawReward({
        trackerId,
        modelCode
      }, buildTrace('confirm')));
      if (result.error || !result.data) {
        logger.error('[error] confirm lucky draw', result);
        trackBigCampaign2026({
          step: trackingStepId,
          value: 'confirm_qr_error',
          groupName: formName
        });
        return;
      }
      setOmoQrCode(result.data);
    } catch (error) {
      logger.error('[error] confirm lucky draw API', error);
      trackBigCampaign2026({
        step: trackingStepId,
        value: 'confirm_qr_error',
        groupName: formName
      });
    }
  };
  const onRetryQrCode = () => {
    if (isRetryingQrCode) return;
    if (!eligibility?.trackerId) return;
    setIsRetryingQrCode(true);
    confirmPrize(eligibility.trackerId, savedForm?.modelCode).finally(() => setIsRetryingQrCode(false));
  };
  useEffect(() => {
    if (!isLine) return;

    // LINE in-app browser blocks downloads: reopen in the native browser.
    const urlParams = new URLSearchParams(location.search);
    urlParams.set('openExternalBrowser', '1');
    const newUrl = new URL(`${location.pathname}?${urlParams.toString()}`, location.origin);
    const redirect = () => {
      window.location.href = newUrl.toString();
    };
    // Trace 02 before leaving (a navigation would cancel the request); at most 800ms.
    Promise.race([logTrace(buildTrace('lineRedirect', {
      fromSearch: sanitizeSearch(location.search),
      toSearch: sanitizeSearch(newUrl.search)
    })).catch(() => undefined), new Promise(resolve => setTimeout(resolve, 800))]).finally(redirect);
  }, [isLine]);
  useEffect(() => {
    // Not on the summary (QR retry).
    if (step === RegisterStep.summary) {
      return;
    }
    if (eligibility === undefined || savedForm === undefined || savedTracking === undefined) {
      return;
    }

    // Receiver: always spins; eligibility only prices it.
    if (isFlowShareLink) {
      trackBigCampaign2026({
        step: trackingStepId,
        groupName: formName,
        value: 'submit_share_receiver',
        text: `modelCode = ${savedForm.modelCode}`
      });
      trackLeadSuccess(savedTracking);
      saveLmsPayload(savedForm);
      setIsShowSpin(true);
      return;
    }

    // Optimistic for DMC conversion check
    trackLeadSuccess(savedTracking);
    if (!eligibility.isEligible && eligibility.isPrizeLocked) {
      // [case]: มีสิทธิ ลงทะเบียนรับสิทธิ์แล้ว แสดงส่วนลดเดิม
      trackBigCampaign2026({
        step: trackingStepId,
        groupName: formName,
        value: 'submit_resubmit_same_phone'
      });
      formTracker.onFailed({
        errorType: 'already_registered'
      });
      openAlreadySubmitted();
      return;
    }
    if (!eligibility.isEligible && !eligibility.isPrizeLocked) {
      // [case]: ไม่มีสิทธิได้รับส่วนลด (Rare case)
      trackBigCampaign2026({
        step: trackingStepId,
        groupName: formName,
        value: 'submit_false'
      });
      formTracker.onFailed({
        errorType: 'not_eligible'
      });
      handleOnError('not_eligible', trackingStepId);
      return;
    }
    trackBigCampaign2026({
      step: trackingStepId,
      groupName: formName,
      value: eligibility.isPrizeLocked ? 'submit_resubmit_different_phone' : 'submit_first_time',
      text: `modelCode = ${savedForm.modelCode}`
    });
    saveLmsPayload(savedForm);
    // Confirm + lead happen at the spin CTA.
    setIsShowSpin(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligibility]);
  useEffect(() => {
    const rafId = paginateTimeoutRef.current;
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);
  const handleSummaryReady = () => {
    setIsNavigatingToSummary(false);
    const funnel = funnelRef.current;
    if (funnel.isSummaryTracked) return;
    funnel.isSummaryTracked = true;
    trackBigCampaign2026({
      step: RegisterStep.summary,
      groupName: formName,
      value: 'summary_view'
    });
  };
  const handleSpinResult = () => {
    const funnel = funnelRef.current;
    if (funnel.isResultTracked) return;
    funnel.isResultTracked = true;
    trackBigCampaign2026({
      step: RegisterStep.spin,
      groupName: formName,
      value: 'spin_result',
      text: `modelCode = ${savedForm?.modelCode}`
    });
  };
  useEffect(() => {
    // Trace 04 (claimer): which model the share link resolved to.
    if (hasRefTrackerId) {
      traceStep('shareModelResolved', {
        queryModelCode: queryParams.modelCode ?? '',
        trackerModelCode: trackerData?.modelCode ?? null,
        shareModelCode,
        sharerModelGroup: sharerModelGroup ?? null,
        isFlowShareLink,
        isInvalidTrackerId
      });
    }
    if (isInvalidTrackerId) {
      trackBigCampaign2026({
        step: RegisterStep.form,
        groupName: formName,
        value: 'share_link_invalid'
      });
      traceStep('errorPopup', {
        popup: 'tracker_not_found',
        reason: 'tracker_not_found',
        stepName: STEP_NAME[RegisterStep.form],
        modelCode: queryParams.modelCode ?? '',
        refTrackerId: queryParams.refTrackerId || null
      }, 'fail');
      return;
    }
    if (isFlowShareLink) {
      trackBigCampaign2026({
        step: RegisterStep.form,
        groupName: formName,
        value: 'reveal_view'
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!initialQueryParams) return;

    // Track lead initiation
    trackLeadInitiated({
      campaignId: FORM_ID,
      campaignName: formName,
      type: variant === 'offer' ? 'financial' : 'interest',
      selectedModel: offerDetail?.model
    });
  }, [initialQueryParams, formName, offerDetail, variant]);
  const renderStep = () => {
    switch (step) {
      case RegisterStep.form:
        return <StepForm variant={variant} imageCover={imageCover} offerDetail={offerDetail} preSelectedDealer={preSelectedDealer} preSelectedModelCode={variant === 'offer' ? preSelectedModelCode : hasRefTrackerId ? undefined : queryParams.modelCode} savedForm={savedForm} provinces={provinces} models={models} formName={formName} trackModel={variant === 'offer' ? offerDetail?.model : queryParams.model} isLoading={isSubmitting || isInvalidTrackerId} clearForm={!savedForm && !!eligibility} tracking={formTracker} onSubmit={handleFormSubmit} />;
      case RegisterStep.summary:
        {
          return (isNavigatingToSummary && !!eligibility || !!eligibility) && <StepSummary formName={formName}
          // Receiver: no QR.
          qrCodeData={isFlowShareLink ? undefined : omoQrCode} data={summaryData ?? eligibility}
          // Receiver: the sharer's name.
          fullname={(isFlowShareLink ? summaryData?.fullName : savedForm?.fullname) ?? ''} citizenId={savedForm?.citizenId} citizenType={savedForm?.type} selectedModel={selectedModel} savedModelCode={savedForm?.modelCode} isFlowShareLink={isFlowShareLink} onReady={handleSummaryReady} onRetryQrCode={onRetryQrCode} />;
        }
      default:
        return null;
    }
  };
  return <LazyMotion features={domAnimation}>
      <div ref={sentinelRef} data-myi-hide className="pointer-events-none absolute top-10 h-px" aria-hidden="true" />

      <h1 className="sr-only">{CAMPAIGN_NAME}</h1>

      {!isShowReveal && <Container size="desktop-only" as="section" className={cn('grid size-full xl:gap-x-6', 'before:fixed before:inset-0', {
      'xl:before:bg-silver-50 before:bg-silver-700': variant === 'default',
      'before:bg-white xl:pb-10': variant === 'offer'
    }, styles.baseLayoutGrid)}>
          <aside className={cn('hidden overflow-hidden rounded-lg xl:block', 'aspect-9-16 my-8 w-94 min-w-94', {
        'relative h-auto min-h-60': variant === 'default',
        'h-full': variant === 'offer'
      })} {...createDataAttributes({
        test: 'aside-image-xl'
      })}>
            {variant === 'default' && <Image src={imageCover.desktop} width={376} height={668} alt="อีซูซุประเทศไทย Isuzu Thailand Official" layout="fill" sources={[{
          srcset: imageCover.desktop,
          options: {
            width: 376
          }
        }]} className="hidden xl:block" priority={isDesktop} />}

            {variant === 'offer' && <SpecialOffersContent imageCover={imageCover} {...offerDetail as SpecialOffer} />}
          </aside>

          <AnimatePresence mode="wait">
            <m.div key={step} className="w-full min-w-0" variants={isDesktop === undefined ? NO_ANIMATION_VARIANTS : isDesktop ? DESKTOP_VARIANTS : VARIANTS} initial="enter" animate="center" exit="exit" transition={{
          ease: easeBounce,
          duration: isDesktop === undefined ? 0 : 0.8
        }}>
              {renderStep()}
            </m.div>
          </AnimatePresence>
        </Container>}

      {!!showErrorToast && <ErrorToast className="top-18" closeButtonAttributes={createDataGA({
      click: true,
      eventName: BIG_CAMPAIGN_2026_EVENT,
      group: STEP_NAME[errorPopup?.step ?? trackingStepId],
      text: errorPopup?.reason,
      value: 'error_popup_close'
    })} onClose={closeErrorToast} />}

      {!!showAlreadySubmitted && <ModalAlreadySubmitted onNextStep={skipToSummary} onClose={handleClearForm} />}

      {!!isInvalidTrackerId && <ModalTrackerNotFound />}

      {!!isShowReveal && <StepReveal sharerName={trackerData?.fullName} onClick={onRevealDone} buttonLabel="รับสิทธิ์ส่วนลด" />}

      {!!isShowSpin && !!eligibility && <StepSpin amount={getDiscountByModel(eligibility.discounts, selectedModel?.modelGroup)?.special || 0} modelName={selectedModel?.model} isLoading={isCollecting} onClick={onSpinDone} onResult={handleSpinResult} />}
    </LazyMotion>;
};